import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, Input, InputGroup,
  InputLeftElement, Select, Table, Thead, Tbody, Tr, Th, Td, IconButton, Tooltip,
  useToast, Circle, Checkbox,
} from "@chakra-ui/react";
import { Phone, MessageSquare, Search, UserPlus, RefreshCw, Users, Trash2 } from "lucide-react";
import {
  getCrmCarteira, criarCrmContato, trocarDonoCrmCarteira, removerCrmCarteira,
} from "../../services/api";
import { useSoftphone } from "../../contexts/softphone";
import { useDebounce } from "../../hooks/useDebounce";
import {
  formatPhone, paraDiscagem, formatBRL, soDigitos, saudeInfo, rotuloSegmento, recenciaLabel,
} from "./crmUtils";
import AdicionarContaModal from "./AdicionarContaModal";

const ACCENT = "#F68633";

export default function CarteiraTab({ onAbrirChat, onAbrirFicha, refreshKey }) {
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saude, setSaude] = useState("");
  const [ordenar, setOrdenar] = useState("");
  // Filtro padrão: a carteira do próprio usuário logado.
  const [vendedor, setVendedor] = useState(
    () => String(JSON.parse(window.localStorage.getItem("user") || "{}")?.id || "")
  );
  const [addOpen, setAddOpen] = useState(false);
  const [abrindo, setAbrindo] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [acao, setAcao] = useState(false);
  const buscaDeb = useDebounce(search, 350);
  const sp = useSoftphone();
  const toast = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    const r = await getCrmCarteira({
      search: buscaDeb || undefined,
      saude: saude || undefined,
      vendedor_id: vendedor || undefined,
      ordenar: ordenar || undefined,
    });
    setResp(r || null);
    setSel(new Set());
    setLoading(false);
  }, [buscaDeb, saude, vendedor, ordenar]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  const ligar = (c) => {
    if (soDigitos(c.telefone).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    if (!sp.registered) { toast({ status: "warning", title: "Ramal desconectado", description: "Conecte o softphone no Discador." }); return; }
    sp.dial(paraDiscagem(c.telefone));
    toast({ status: "info", title: "Discando...", description: formatPhone(c.telefone) });
  };

  const conversar = async (c) => {
    if (soDigitos(c.telefone).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    setAbrindo(c.cliente_id);
    const r = await criarCrmContato({ telefone: c.telefone, nome: c.nome, origem: "carteira" });
    setAbrindo(null);
    if (r?.data?.id) onAbrirChat(r.data.id);
    else toast({ status: "error", title: "Erro", description: r?.error || "Não foi possível abrir a conversa." });
  };

  const linhas = resp?.data || [];
  const isAdmin = resp?.is_admin;
  const periodo = resp?.rfm_periodo_dias;
  const vendedores = resp?.vendedores || [];

  const toggleSel = (id) => setSel((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleTodas = () =>
    setSel((s) => (s.size === linhas.length ? new Set() : new Set(linhas.map((l) => l.cliente_id))));

  const removerSelecionadas = async () => {
    if (!sel.size) return;
    if (!window.confirm(`Remover ${sel.size} conta(s) da carteira? Elas voltam para a Base, sem dono (não são excluídas).`)) return;
    setAcao(true);
    await Promise.all([...sel].map((id) => removerCrmCarteira(id)));
    setAcao(false);
    toast({ status: "success", title: `${sel.size} conta(s) removida(s) da carteira.` });
    carregar();
  };

  const trocarDono = async (vendedorId) => {
    if (!sel.size || !vendedorId) return;
    const qtd = sel.size;
    setAcao(true);
    await Promise.all([...sel].map((id) => trocarDonoCrmCarteira(id, Number(vendedorId))));
    setAcao(false);
    const nome = vendedores.find((v) => String(v.id) === String(vendedorId))?.name || "vendedor";
    toast({ status: "success", title: `${qtd} conta(s) atribuída(s) a ${nome}.` });
    carregar();
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
        <HStack spacing={2} flex="1" flexWrap="wrap">
          <InputGroup size="sm" maxW="280px">
            <InputLeftElement pointerEvents="none"><Search size={15} color="#999" /></InputLeftElement>
            <Input placeholder="Buscar nome, CNPJ/CPF ou telefone..." value={search} onChange={(e) => setSearch(e.target.value)} bg="white" />
          </InputGroup>
          <Select size="sm" maxW="150px" bg="white" placeholder="Toda saúde" value={saude} onChange={(e) => setSaude(e.target.value)}>
            <option value="vermelho">Em risco</option>
            <option value="amarelo">Atenção</option>
            <option value="verde">Saudável</option>
            <option value="sem_compra">Sem compra</option>
          </Select>
          <Select size="sm" maxW="160px" bg="white" placeholder="Ordem padrão" value={ordenar} onChange={(e) => setOrdenar(e.target.value)}>
            <option value="valor_desc">Maior valor</option>
            <option value="valor_asc">Menor valor</option>
          </Select>
          {isAdmin && (
            <Select size="sm" maxW="190px" bg="white" placeholder="Todos vendedores" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Select>
          )}
        </HStack>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={carregar}>Atualizar</Button>
          <Button size="sm" colorScheme="orange" leftIcon={<UserPlus size={15} />} onClick={() => setAddOpen(true)}>Adicionar conta</Button>
        </HStack>
      </Flex>

      {sel.size > 0 && (
        <Flex bg="orange.50" border="1px solid" borderColor="orange.100" borderRadius="md" p={2} mb={3} align="center" gap={3} flexWrap="wrap">
          <Text fontSize="sm" fontWeight="medium">{sel.size} selecionada(s)</Text>
          <Select size="sm" maxW="220px" bg="white" placeholder="Trocar vendedor para..." isDisabled={acao}
            value="" onChange={(e) => trocarDono(e.target.value)}>
            {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </Select>
          <Button size="sm" colorScheme="red" variant="outline" leftIcon={<Trash2 size={14} />} isLoading={acao} onClick={removerSelecionadas}>
            Remover da carteira
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSel(new Set())}>Cancelar</Button>
        </Flex>
      )}

      {periodo && <Text fontSize="xs" color="gray.400" mb={2}>Frequência e valor consideram os últimos {periodo} dias.</Text>}

      {loading ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : linhas.length === 0 ? (
        <Center py={16} flexDirection="column" color="gray.400">
          <Users size={40} />
          <Text mt={3} fontSize="sm">Nenhuma conta na carteira.</Text>
          <Text fontSize="xs">Use "Adicionar conta" para começar.</Text>
        </Center>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th px={2}>
                  <Checkbox
                    isChecked={sel.size > 0 && sel.size === linhas.length}
                    isIndeterminate={sel.size > 0 && sel.size < linhas.length}
                    onChange={toggleTodas}
                  />
                </Th>
                <Th>Cliente</Th>
                <Th>Saúde</Th>
                <Th>Última compra</Th>
                <Th isNumeric>Pedidos</Th>
                <Th isNumeric>Valor</Th>
                {isAdmin && <Th>Vendedor</Th>}
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {linhas.map((c) => {
                const s = saudeInfo(c.saude);
                return (
                  <Tr key={c.cliente_id} _hover={{ bg: "orange.50", cursor: "pointer" }} onClick={() => onAbrirFicha(c.cliente_id)}>
                    <Td px={2} onClick={(e) => e.stopPropagation()}>
                      <Checkbox isChecked={sel.has(c.cliente_id)} onChange={() => toggleSel(c.cliente_id)} />
                    </Td>
                    <Td>
                      <Box minW={0}>
                        <HStack spacing={1}>
                          <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{c.nome || "Sem nome"}</Text>
                          {c.tipo_doc && <Badge fontSize="9px" colorScheme={c.tipo_doc === "CNPJ" ? "purple" : "blue"}>{c.tipo_doc === "CNPJ" ? "PJ" : "PF"}</Badge>}
                        </HStack>
                        <Text fontSize="xs" color="gray.400" noOfLines={1}>
                          {c.tipo_segmento ? rotuloSegmento(c.tipo_segmento) + " · " : ""}{formatPhone(c.telefone) || "sem telefone"}
                        </Text>
                      </Box>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Circle size="10px" bg={s.hex} />
                        <Text fontSize="xs" color="gray.600">{s.label}</Text>
                      </HStack>
                    </Td>
                    <Td><Text fontSize="xs" color="gray.600">{recenciaLabel(c.recencia_dias)}</Text></Td>
                    <Td isNumeric><Text fontSize="sm">{c.frequencia}</Text></Td>
                    <Td isNumeric><Text fontSize="sm" fontWeight="medium">{formatBRL(c.valor_periodo)}</Text></Td>
                    {isAdmin && <Td><Text fontSize="xs" color="gray.500" noOfLines={1}>{c.vendedor_nome || "—"}</Text></Td>}
                    <Td onClick={(e) => e.stopPropagation()}>
                      <HStack spacing={1} justify="end">
                        <Tooltip label="Ligar"><IconButton aria-label="Ligar" size="xs" variant="ghost" colorScheme="green" icon={<Phone size={15} />} onClick={() => ligar(c)} /></Tooltip>
                        <Tooltip label="Conversar"><IconButton aria-label="Conversar" size="xs" variant="ghost" colorScheme="orange" icon={<MessageSquare size={15} />} isLoading={abrindo === c.cliente_id} onClick={() => conversar(c)} /></Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <AdicionarContaModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={carregar}
        vendedores={resp?.vendedores}
        isAdmin={isAdmin}
      />
    </Box>
  );
}
